import { TimeInput24Hr } from "@/components/TimeInput";

export enum TimeMeridiem {
	AM = "AM",
	PM = "PM",
}

export function determineMeridiemFrom24Hr(hour: number): TimeMeridiem {
	return hour >= 12 ? TimeMeridiem.PM : TimeMeridiem.AM;
}

/**
 * Converts a 24-hour format hour to 12-hour format
 *
 * e.g. 15 -> 3
 * e.g. 0 -> 12
 * e.g. 12 -> 12
 * e.g. 23 -> 11
 */
export function convert24HrTo12Hr(hour24: number): number {
	if (hour24 === 0) {
		return 12; // midnight -> 12 AM
	} else if (hour24 === 12) {
		return 12; // noon -> 12 PM
	} else if (hour24 > 12) {
		return hour24 - 12; // PM hours
	} else {
		return hour24; // AM hours (1-11)
	}
}

export function isDateInThePast(date: Date): boolean {
	return date < new Date();
}

/**
 * Formats time as "HH:MM AM/PM" from a string in the 24 hour format "HH:MM"
 */
export function format24HrStringAs12Hr(timeString: string): string {
	const [hours, minutes] = timeString.split(":");
	const hoursInt = parseInt(hours);
	const minutesInt = parseInt(minutes);
	const hours12 = hoursInt % 12 || 12;
	return `${hours12}:${minutesInt.toString().padStart(2, "0")} ${determineMeridiemFrom24Hr(hoursInt)}`;
}

/**
 * Formats {@link TimeInput24Hr} as "HH:MM AM/PM"
 */
export function format24HrTimeInputAs12Hr(time: TimeInput24Hr): string {
	const hour12 = time.hour % 12 || 12;
	return `${hour12}:${time.minute.toString().padStart(2, "0")} ${determineMeridiemFrom24Hr(time.hour)}`;
}

/**
 * Formats {@link TimeInput24Hr} for UI: 24-hour `HH:mm` when `use24Hour`, otherwise 12-hour with AM/PM.
 */
export function format24HrTimeInputForDisplay(time: TimeInput24Hr, use24Hour: boolean): string {
	if (use24Hour) {
		return `${time.hour.toString().padStart(2, "0")}:${time.minute.toString().padStart(2, "0")}`;
	}
	return format24HrTimeInputAs12Hr(time);
}

/**
 * Formats a date object as "Mon DD, YYYY"
 */
export function formatDateAsLocaleDateString(date: Date): string {
	return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Formats a Date object as DD/MM/YYYY string
 *
 * This is used to send the date to the API in the "DD/MM/YYYY" format it expects.
 */
export function formatDateAsDDMMYYYY(date: Date): string {
	const day = date.getDate().toString().padStart(2, "0");
	const month = (date.getMonth() + 1).toString().padStart(2, "0"); // getMonth() is 0-indexed
	const year = date.getFullYear();
	return `${day}/${month}/${year}`;
}

/**
 * Get date object from DD/MM/YYYY string
 */
export function getDateFromDDMMYYYY(dateString: string): Date {
	const [day, month, year] = dateString.split("/");
	return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
}
